/** Link handling for Team Log post bodies: safe links and runner screenshots. */

/** Screenshots under this exact prefix are shown inline in visual-check results. */
export const RUNNER_IMAGE_PREFIX = "https://storage.googleapis.com/relay-runner-tber-visual-runner/";

export type LogLinkSegment =
  | { kind: "text"; text: string }
  | { kind: "link"; label: string; url: string };

/** Only http(s) URLs may become links; javascript:, data:, file: and the rest stay text. */
export function isSafeLinkUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

/** True for a link whose URL starts with the runner bucket prefix. */
export function isRunnerImageUrl(url: string): boolean {
  return url.startsWith(RUNNER_IMAGE_PREFIX) && isSafeLinkUrl(url);
}

// [label](url) or a bare http(s) URL.
const LINK = /\[([^\]\n]*)\]\(([^\s)]+)\)|\bhttps?:\/\/[^\s<>()[\]]+/g;
const TRAILING_PUNCT = /[.,;:!?'"]+$/;

/** Split text into plain text and safe links. Unsafe markdown links stay as their raw text. */
export function parseLogLinks(text: string): LogLinkSegment[] {
  const out: LogLinkSegment[] = [];
  let last = 0;
  const pushText = (t: string) => {
    if (!t) return;
    const prev = out[out.length - 1];
    if (prev?.kind === "text") prev.text += t;
    else out.push({ kind: "text", text: t });
  };
  for (const m of text.matchAll(LINK)) {
    pushText(text.slice(last, m.index));
    let whole = m[0];
    if (m[2] !== undefined) {
      if (isSafeLinkUrl(m[2])) out.push({ kind: "link", label: m[1] || m[2], url: m[2] });
      else pushText(whole);
    } else {
      const trail = TRAILING_PUNCT.exec(whole)?.[0] ?? "";
      const url = whole.slice(0, whole.length - trail.length);
      if (isSafeLinkUrl(url)) {
        out.push({ kind: "link", label: url, url });
        pushText(trail);
      } else pushText(whole);
    }
    last = m.index + whole.length;
  }
  pushText(text.slice(last));
  return out;
}
